import React, {ReactElement, useEffect, useState} from "react";
import ProjectDetailsLayout from "@/components/layouts/ProjectDetailsLayout";
import {useAuth} from "@/libs/auth";
import {getUsage} from "@/services/usage-store";
import {processDate, processEpoch} from "@/utils/date";
import {Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import Dropdown from "@/components/general/dropdowns/Dropdown";
import LoadingDots from "@/components/general/LoadingDots";
import {useRouter} from "next/router";
import {retrieveCustomerSubscription, retrieveProduct} from "@/services/stripe-store";

type TimeseriesPoint = { time: number, value: number };
type Data = { creditUsage: number, cpuData: TimeseriesPoint[], ramData: TimeseriesPoint[] }
const defaultUsageData: Data = {
  creditUsage: -1, cpuData: [], ramData: []
}

const ProjectUsage = () => {
  const router = useRouter();
  const {projectId} = router.query;
  const {uid, user, token} = useAuth();
  const [usageData, setUsageData] = useState(defaultUsageData)
  const [monthIdx, setMonthIdx] = useState(0);

  useEffect(() => {
    if (!user) return;
    setUsageData(defaultUsageData) // reset state before fetching

    const [startTime, endTime] = getMonthEpochs(monthChoices[monthIdx]);

    getUsage(token, startTime, endTime, projectId as string)
      .then(res => {
        const {cpu, ram, credits} = res.data;
        setUsageData({
          creditUsage: credits,
          cpuData: cpu,
          ramData: ram
        })
      })
  }, [user, monthIdx, token, projectId, user]);

  const [credits, setCredits] = useState(2000)
  useEffect(() => {
    if (!user?.cid) return;

    retrieveCustomerSubscription(token, user.cid).then((res: any) => {
      const productId = res.data.items.data[0].plan.product
      if (!productId) return;
      retrieveProduct(token, productId).then((res) => {
        setCredits(Number(res.data.metadata.credit))
      })
    })
  }, [token, user.cid])

  const {cpuData, ramData, creditUsage} = usageData;
  const monthChoices = generateMonthChoices();
  const month = monthChoices[monthIdx];
  const monthName = processDate(month, "MMMM");
  const isPresentMonth = monthIdx === 0;

  return (
    <div className="pt-12 pb-32">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-medium mb-4 mt-4">Usage Details</h1>
            <p className="text-gray-400 mb-2">
              {isPresentMonth
                ? `Current credit usage this month: `
                : `Total credit usage for the month of ${monthName}: `}

              {creditUsage < 0
                ? <LoadingDots/>
                : <span className="text-red-500">{creditUsage?.toLocaleString()} Credits</span>}
            </p>
            <p className="text-gray-400 mb-2">
              Total free credits given by your {user?.currPlan?.toLowerCase()} plan: {" "}
              <span className="text-blue-400">
                {credits} Credits
              </span>
            </p>
            <p className="text-gray-400 mb-8">
              Usage stats for all your models over
              the {isPresentMonth ? "past month" : `month of ${monthName}`} is shown below:
            </p>
          </div>
          <div className="sm:w-64 w-full mt-5 sm:mt-0">
            <Dropdown update={(e: any) => setMonthIdx(e.idx)}
                      options={monthChoices.map((date: Date, idx) => {
                        return {label: processDate(date, "MMMM YYYY"), idx: idx}
                      })}
                      default={monthIdx}/>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="border border-gray-200 rounded-md px-8">
            <h2 className="text-2xl font-medium mt-12 mb-4">CPU Usage</h2>
            <Chart data={cpuData} yAxisName="CPU Usage" color={CPU_COLOR} month={month}/>
          </div>
          <div className="mt-5 border border-gray-200 rounded-md px-8">
            <h2 className="text-2xl font-medium mt-12 mb-4">Memory Usage</h2>
            <Chart data={ramData} yAxisName="Memory Usage (MiB)" color={RAM_COLOR} month={month}/>
          </div>
        </div>
      </div>
    </div>
  );
}

const RAM_COLOR = "#8884d8"
const CPU_COLOR = "#82ca9d"

function Chart({data, yAxisName, color, month}: { data: any, yAxisName: string, color: string, month: Date }) {
  const gradient = color.slice(1);
  return <ResponsiveContainer width="100%" aspect={3}>
    <AreaChart data={data}
               height={500}
               width={1180}
               margin={{
                 top: 5,
                 right: 0,
                 left: 0,
                 bottom: 5,
               }}>
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
          <stop offset="95%" stopColor={color} stopOpacity={0}/>
        </linearGradient>
      </defs>
      <XAxis name="Time"
             dataKey="time"
             type="number"
             domain={getMonthEpochs(month)}
             tickFormatter={time => processEpoch(time, 'MMM Do')}/>
      <YAxis type="number"
             domain={['auto', 'dataMax']}/>
      <CartesianGrid strokeDasharray="3 3"/>
      <Tooltip labelFormatter={(time) => processEpoch(time, 'MMM Do HH:mm')}
               formatter={(value: number) => value.toFixed(2)}/>
      <Legend/>
      <Area type="monotone" name={yAxisName} connectNulls={true} dataKey="value" stroke={color} fillOpacity={1}
            fill={`url(#${gradient})`}/>
      <Area type="monotone" name={'Requested ' + yAxisName} connectNulls={true} dataKey="requested" stroke={color}
            fillOpacity={0.1}
            fill={`url(#${gradient})`}/>
    </AreaChart>
  </ResponsiveContainer>;
}

const generateMonthChoices = (length: number = 12) => {
  const now = new Date();
  return Array.from({length: length},
    (_, i) => {
      let year = now.getFullYear();
      let month = now.getMonth() - i;
      while (month < 0) {
        month += 12;
        year--;
      }
      return new Date(year, month, 1)
    })
}

const getMonth = (day: Date): Date[] => {
  return [
    new Date(day.getFullYear(), day.getMonth(), 1),
    new Date(day.getFullYear(), day.getMonth() + 1, 0)
  ];
}

const getMonthEpochs = (day: Date): number[] => getMonth(day).map((date: Date) => Math.trunc(date.getTime() / 1000))

ProjectUsage.getLayout = function getLayout(page: ReactElement) {
  return <ProjectDetailsLayout>{page}</ProjectDetailsLayout>
}

export default ProjectUsage;
